import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardMedia,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  analytics,
  WrapperStyles,
  getUsername,
  formatEpochToDate,
} from "../../shared/utils";
import type { FBSession, FBUser } from "../../shared/types";
import {
  fetchUser,
  fetchSession,
  fetchSessionOwner,
} from "../../shared/services";
import DownloadOverlay from "../overlays/DownloadOverlay";
import FooterOverlay from "../overlays/FooterOverlay";
import LoadingOverlay from "../overlays/LoadingOverlay";
import DynamicMeta from "../shared/DynamicMeta";

const PublicSessionView = () => {
  const { id: sessionId } = useParams();
  const [loading, setLoading] = useState<boolean>(true);
  const [session, setSession] = useState<FBSession>();
  const [user, setUser] = useState<FBUser>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Fetch referrer id if passed
  const [searchParams] = useSearchParams();
  const referrerId = searchParams.get("referrerId");

  useEffect(() => {
    analytics.screen("Web/Public/Session", {
      sessionId: sessionId,
      referrerId: referrerId,
    });
  }, [sessionId, referrerId]);

  useEffect(() => {
    if (!sessionId) {
      setLoading(false);
      return;
    }
    setLoading(true);

    // Conditionally fetch user based on referrerId
    const userPromise = referrerId
      ? fetchUser(referrerId)
      : fetchSessionOwner(sessionId);

    Promise.all([fetchSession(sessionId), userPromise])
      .then(([session, user]) => {
        setSession(session);
        setUser(user);
      })
      .catch((error) => {
        console.error(error);
        setUser(undefined);
        setSession(undefined);
      })
      .finally(() => setLoading(false));
  }, [sessionId, referrerId]);

  const SessionOverlay = () => {
    if (!user || !session) return null;
    const name = getUsername(user);
    const date = formatEpochToDate(session.displayTime ?? session.createdAt);
    return (
      <Card
        sx={{
          display: "flex",
          backgroundColor: "rgba(0, 0, 0, 0.87)",
          alignItems: "center",
        }}
      >
        <CardContent sx={{ flex: "1 0 auto" }}>
          <Box
            sx={{
              justifyContent: "center",
              display: "flex",
              alignItems: "center",
              gap: 1,
              maxWidth: 200,
            }}
          >
            {user.profilePicture && (
              <Avatar
                src={user.profilePicture}
                alt={name}
                sx={{ width: 24, height: 24 }}
              />
            )}
            {name ? (
              <Typography
                sx={{
                  color: "white",
                  fontSize: !isMobile ? 18 : 16,
                  fontWeight: "bold",
                  textAlign: "left",
                  numberOfLines: 2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {name} shared a game from {date}
              </Typography>
            ) : (
              <Typography
                sx={{
                  color: "white",
                  fontSize: !isMobile ? 18 : 16,
                  fontWeight: "bold",
                  textAlign: "left",
                  numberOfLines: 2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                A Hooper game from {date}
              </Typography>
            )}
          </Box>
        </CardContent>
        {session.videoPreview && (
          <CardMedia
            component="img"
            sx={{
              width: 151,
              alignSelf: "stretch",
              objectFit: "cover",
            }}
            image={session.videoPreview}
            alt="Session video preview image"
          />
        )}
      </Card>
    );
  };

  const getMetaTitle = () => {
    if (!session) return "Try Hooper today - download from the app store";
    const name = (user && getUsername(user)) || "Someone";
    const datePart = session.createdAt
      ? ` - ${formatEpochToDate(session.displayTime ?? session.createdAt)}`
      : "";
    return `${name}'s Basketball Session${datePart}`;
  };

  const getMetaDescription = () => {
    if (!session) return "Create your stats and highlights, shot by shot";
    const name = (user && getUsername(user)) || "someone";
    const datePart = session.createdAt
      ? ` from ${formatEpochToDate(session.displayTime ?? session.createdAt)}`
      : "";
    return `Check out ${name}'s basketball session${datePart}. See their stats, highlights, and performance on Hooper.`;
  };

  const getMetaImage = () => {
    if (session?.videoPreview) return session.videoPreview;
    if (user?.profilePicture) return user.profilePicture;
    return "/logos/logo-white.svg";
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        minHeight: "100vh",
        flexDirection: "column",
        display: "flex",
        px: "0 !important",
      }}
    >
      <DynamicMeta
        title={getMetaTitle()}
        description={getMetaDescription()}
        image={getMetaImage()}
        type="article"
        url={`/session/${sessionId}${referrerId ? `?referrerId=${referrerId}` : ""}`}
      />
      <Box sx={WrapperStyles}>
        <Box style={{ marginTop: 100 }} />
        {loading ? (
          <LoadingOverlay />
        ) : (
          <DownloadOverlay
            parentViewName="Session"
            backgroundId="background-1.jpg"
            deepLinkPath={`/session/${sessionId}${referrerId ? `?referrerId=${referrerId}` : ""}`}
            children={<SessionOverlay />}
          />
        )}
        <Box sx={{ my: 8 }} />
        <FooterOverlay />
      </Box>
    </Container>
  );
};

export default PublicSessionView;
